import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { of } from 'rxjs';
import { catchError, concatMap, map } from 'rxjs/operators';

import { LicenseScopeType, ScopeFunctionInfo } from '../../../../../app/content/license/license.data';
import { PolicyInfo, PolicyType } from '../../../../../app/content/setting/policy/policy.data';
import { PolicyService } from '../../../../../app/content/setting/policy/policy.service';
import { HelperLib } from '../../../../lib/common/helper.lib';
import { DeviceGroupInfo } from '../../group/dev-group.data';
import { DeviceGroupService } from '../../group/dev-group.service';
import { DeviceInfo, IPolicyLockMap } from '../../data/device-info';
import { AccountService } from '../../../../entry/account.service';
import { ConfigDialogPage } from '../../../../../app/content/devfunc/base/base-config-data';
import { DevFuncDirective } from '../../../../../app/content/devfunc/dev-func.directive';
import { DevFuncService } from '../../../../../app/content/devfunc/dev-func.service';
import { DevFuncItem } from '../../../../../app/content/devfunc/dev-func-item';
import { DevFuncInterface } from '../../../../../app/content/devfunc/dev-func.interface';
import { Logger } from '../../../../../app/lib/common/logger';
import { IClass } from '../../../../../app/lib/common/common.data';
import { ConstantService } from '../../../../../app/lib/common/constant.service';
import { NAService } from '../../../../../app/API/na.service';

@Component({
    selector: 'na-device-policy-info',
    templateUrl: './device-info-policy.component.html',
    styleUrls: ['./device.style.css', '../device-detail.component.css', './device-info-policy.component.css'],
    standalone: false
})
export class DeviceInfoPolicyComponent implements IClass {
    readonly TEXT_LOADING: string = 'loading ...';
    readonly TEXT_PENDING: string = 'pending';
    readonly TEXT_SYNCED: string = 'synced';
    readonly TEXT_PAUSED: string = 'paused';

    readonly FORCE_CALCULATE_POLICY_SYNC_DURATION_MS: number = 30 * 1000; // 30 seconds

    className: string = 'DeviceInfoPolicyComp';
    _page: ConfigDialogPage = ConfigDialogPage.action;
    _enumPage: typeof ConfigDialogPage = ConfigDialogPage;
    _errorMessage: string;

    _loading: boolean = false;
    _bConfigPolicyPauseStatus: boolean = false;
    _policyMap: { [name: string]: { list: PolicyInfo[], status?: string, isPartialSync?: boolean, partialSyncDescList?: string[] } } = {};
    _enumLicenseScope: typeof LicenseScopeType = LicenseScopeType;

    _pauseEndDateTime: Date;
    _isPolicyPaused: boolean = false;

    _forcePolicySyncRequestCount: number = 0;

    @Input('device') _device: DeviceInfo;
    private _policyLockMap: IPolicyLockMap = {};
    @Input('lock')
    set lockMap(v: IPolicyLockMap) {
        this._policyLockMap = v || {};
        this._isPolicyPaused = this._policyLockMap[this.constantSvc.DEVKEY_FAKE_POLICY_PAUSED] ? true : false;
        this._pauseEndDateTime = this._policyLockMap[this.constantSvc.DEVKEY_FAKE_POLICY_PAUSE_DURATION] as any;
        this.updatePolicies();
    }
    @Input('licenseScope') _licenseScopeMap: { [scopeType: string]: ScopeFunctionInfo } = {};

    @ViewChild(DevFuncDirective, { static: true }) devFuncHost: DevFuncDirective;

    @Output() onPolicyPauseActionTriggered = new EventEmitter<void>();
    @Output() onPolicySyncRecalculateRequested = new EventEmitter<void>();

    constructor(
        private naSvc: NAService,
        private constantSvc: ConstantService,
        private policySvc: PolicyService,
        private devGroupSvc: DeviceGroupService,
        private accountSvc: AccountService,
        private devFuncSvc: DevFuncService
    ) { }

    private updatePolicies(forceRefresh?: boolean): void {
        Logger.logInfo(this.className, 'updatePolicies', 'Begin');
        this._loading = true;

        HelperLib.checkState(1, () => { return this.devGroupSvc.isReady }, () => {
            of(true).pipe(
                concatMap(() => {
                    if (!this._device || !this._device.groupID) {
                        throw 'No device or device group information';
                    }

                    if (!this._licenseScopeMap[LicenseScopeType.devicePolicy]) {
                        throw 'No license scope "devicePolicy"';
                    }

                    let allPolicyIDList: string[] = [];
                    const supportedPolicyTypeList: PolicyType[] = this.policySvc.getSupportPolicyTypesByLevel(this.accountSvc.isEnterprise());
                    //check policy from device group
                    const g: DeviceGroupInfo = this.devGroupSvc.getGroupByID(this._device.groupID);
                    if (g && g.policies) {
                        allPolicyIDList = allPolicyIDList.concat(...supportedPolicyTypeList.map(type => g.policies[type]));
                    }

                    if (allPolicyIDList.length === 0) {
                        throw 'No policy is assigned on this device';
                    }

                    const policyTypeMap: {
                        [type: string]: {
                            isSync: boolean;
                            isPartialSync?: boolean;
                            partialSyncDesc?: string;
                            policyID: string;
                            policyName: string;
                            policyType: string;
                            keyName: string;
                        }[]
                    } = Object.keys(this._policyLockMap).map(key => Object.assign(this._policyLockMap[key], { keyName: key })).reduce((acc, curr) => {
                        acc[curr.policyType] = acc[curr.policyType] || [];
                        acc[curr.policyType].push(curr);

                        return acc;
                    }, {});

                    return this.policySvc.getPolicyByIDList(allPolicyIDList, forceRefresh).pipe(
                        map((policyList: PolicyInfo[]) => {
                            supportedPolicyTypeList.forEach((policyType: PolicyType) => {
                                this._policyMap[policyType] = { list: policyList.filter(p => p.type === policyType) };

                                if (policyTypeMap[policyType]?.length > 0) {
                                    this._policyMap[policyType].status = policyTypeMap[policyType].filter(l => !l.isSync).length > 0 ? this.TEXT_PENDING : this.TEXT_SYNCED;
                                    if (this._policyMap[policyType].status === this.TEXT_SYNCED) {
                                        this._policyMap[policyType].partialSyncDescList = policyTypeMap[policyType].filter(l => l.isPartialSync).map(l => l.partialSyncDesc);
                                        this._policyMap[policyType].isPartialSync = this._policyMap[policyType].partialSyncDescList.length > 0 ? true : false;
                                    }
                                }
                                else if (this._policyMap[policyType].list?.length > 0) {
                                    // No shadow (no setting in the policy), it should be synced
                                    this._policyMap[policyType].status = this.TEXT_SYNCED;
                                }
                            });

                            return '';
                        })
                    );
                }),
                catchError(error => of(error))
            ).subscribe((errorMessage: string) => {
                this._loading = false;
                this._errorMessage = errorMessage;

                if (this._pauseEndDateTime && this._pauseEndDateTime.getTime() < Date.now()) {
                    Logger.logInfo(this.className, 'updatePolicies', 'Recalculate shadow');
                    setTimeout(() => {
                        this.onPolicySyncRecalculateRequested.emit();
                    }, Math.min(this._forcePolicySyncRequestCount++, 4) * this.FORCE_CALCULATE_POLICY_SYNC_DURATION_MS);   
                }
            });
        });
    }

    private submitPolicyConfigComplete(pauseUntilTimestamp: number): void {
        Logger.logInfo(this.className, 'submitPolicyConfigComplete', 'Data = ', pauseUntilTimestamp);
        this._pauseEndDateTime = new Date(pauseUntilTimestamp);
        this._isPolicyPaused = true;
        this._forcePolicySyncRequestCount = 0;

        this.onPolicyPauseActionTriggered.emit();
    }

    pausePolicy(): void {
        const devFuncItem: DevFuncItem = this.devFuncSvc.getFunctionByName(this.devFuncSvc.FUNCNAME_POLICY_PAUSE);
        if (devFuncItem) {
            const viewContainerRef = this.devFuncHost.viewContainerRef;
            viewContainerRef.clear();

            const componentRef = viewContainerRef.createComponent(devFuncItem.component);

            (<DevFuncInterface>componentRef.instance).minWidth = 1000;
            (<DevFuncInterface>componentRef.instance).title = devFuncItem.title;
            (<DevFuncInterface>componentRef.instance).devices = this._device;
            (<DevFuncInterface>componentRef.instance).dialogCompleteHandler = this.submitPolicyConfigComplete.bind(this);
        }
    }

    resumePolicy(): void {
        this._bConfigPolicyPauseStatus = true;

        this._loading = true;
        this.naSvc.pauseVirtualDeviceShadow({ virtualDeviceID: this._device.virtualId }, 0, this.accountSvc.token).subscribe((pausePolicyRet: any) => {
            this._bConfigPolicyPauseStatus = false;
            this._isPolicyPaused = false;

            this.onPolicyPauseActionTriggered.emit();
        });
    }
}